import React, { useState, useEffect } from 'react';
import { Calculator, Users, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const OrganicSocialMediaReachCalculator = () => {
  const [totalFollowers, setTotalFollowers] = useState(10000);
  const [postReach, setPostReach] = useState(2000);
  const [organicReachRate, setOrganicReachRate] = useState(0);

  useEffect(() => {
    calculateOrganicReachRate();
  }, [totalFollowers, postReach]);

  const calculateOrganicReachRate = () => {
    const rate = (postReach / totalFollowers) * 100;
    setOrganicReachRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/organic-social-media-reach-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Organic Social Media Reach Calculator: Measure Post Performance | Free Tool</title>
        <meta name="description" content="Calculate your Organic Social Media Reach instantly. Compare with platform benchmarks, get optimization tips, and improve content visibility." />
        <meta name="keywords" content="organic reach calculator, social media metrics, content visibility, post performance, reach optimization" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Organic Reach Calculator: Optimize Social Media Visibility" />
        <meta property="og:description" content="Calculate your Organic Social Media Reach instantly. Get actionable insights to improve content visibility and engagement." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Organic Reach Calculator: Optimize Social Media Visibility" />
        <meta property="twitter:description" content="Calculate your Organic Social Media Reach instantly. Get actionable insights to improve content visibility and engagement." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Organic Social Media Reach Calculator",
            "description": "Free calculator to measure and optimize organic social media content reach",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Organic Reach",
              "Platform Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Organic Social Media Reach",
              "description": "The percentage of followers who see your social media content without paid promotion."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Social Media Managers, Content Creators, Digital Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Organic Social Media Reach",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total followers"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input post reach"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your reach rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Organic Social Media Reach Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Followers</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalFollowers}
                  onChange={(e) => setTotalFollowers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Post Reach</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={postReach}
                  onChange={(e) => setPostReach(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="2000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {organicReachRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Organic Reach Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Platform Benchmarks</h2>
        <p className="text-gray-600">
          Your Organic Social Media Reach Rate is {organicReachRate}%. This means that {organicReachRate}% of your followers are seeing your posts organically.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Platform Reach Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Facebook: 2-5%</li>
          <li>Instagram: 9-15%</li>
          <li>Twitter: 3-8%</li>
          <li>LinkedIn: 4-7%</li>
          <li>TikTok: 118-200%</li>
          <li>Pinterest: 2-4%</li>
          <li>YouTube: 6-12%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Reach Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Post consistently</li>
          <li>Optimize timing</li>
          <li>Create engaging content</li>
          <li>Use relevant hashtags</li>
          <li>Encourage notifications</li>
          <li>Engage with audience</li>
          <li>Test content formats</li>
          <li>Leverage UGC</li>
          <li>Cross-promote content</li>
          <li>Monitor analytics</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Content Performance Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Post Type</li>
          <li>Posting Time</li>
          <li>Engagement Rate</li>
          <li>Content Quality</li>
          <li>Hashtag Strategy</li>
          <li>Audience Activity</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Video content: +135% reach</li>
          <li>Optimal timing: +25% reach</li>
          <li>Hashtag optimization: +40% reach</li>
          <li>Engagement focus: +70% reach</li>
          <li>Cross-platform sharing: +45% reach</li>
        </ul>
      </div>
    </div>
  );
};

export default OrganicSocialMediaReachCalculator;
